import {Skeleton, view} from '@sail/ui';
import {useIsWalletConnecting} from 'src/v2/components/walletConnect/useIsWalletConnecting';
import {useSelector, setValue} from 'src/v2/state/store';
import {useSingleRunEffect} from 'src/v2/util/useSingleRunEffect';
import {useExpiryCountdown} from 'src/v2/components/walletConnect/useCountdown';
import {Text} from 'src/v2/components/common';
import {getWalletConnectExpiryTime} from 'src/v2/util/parseWalletConnectUri';
import {useCallback, useEffect} from 'react';
import {handleError} from 'src/v2/components/error/handleError';
import {QrCode} from 'src/v2/components/common/QrCode';

import {message, useIntl} from '@sail/intl';

const messages = {
  title: message<{
    walletProvider: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.wallet_connect.qr_code.title',
    description: 'Title for connect a new wallet through scanning a QR code',
    defaultMessage: 'Connect to {walletProvider}',
  }),

  description: message({
    id: 'crypto.payins.v2.wallet_connect.qr_code.description',

    description:
      'Description for connect a new wallet through scanning a QR code',

    defaultMessage:
      "Scan this QR code from your mobile wallet or phone's camera to connect.",
  }),

  countdown: message<{
    timeRemaining: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.wallet_connect.qr_code.countdown',
    description: 'Countdown timer for QR code expiration',
    defaultMessage: 'This will refresh automatically in {timeRemaining}',
  }),
};

const QR_CODE_SIZE = 266;

export const WalletConnectQrCode = () => {
  const intl = useIntl();
  const {startWalletConnection} = useIsWalletConnecting();
  const qrCodeUri = useSelector(
    (state) => state.walletConnection.walletConnectQrCodeUri,
  );
  const newWallet = useSelector((state) => state.newWallet);

  if (newWallet?.walletOption?.provider !== 'WalletConnect') {
    throw new Error('newWallet needs to be WalletConnect');
  }

  const {key: walletKey, iconUrl, name} = newWallet.walletOption;

  const expiryTimestamp = qrCodeUri
    ? getWalletConnectExpiryTime(qrCodeUri)
    : null;

  // Create a refresh function that generates a new QR code
  const refreshQrCode = useCallback(async () => {
    try {
      // Clear existing QR code URI first
      setValue((state) => {
        state.walletConnection.walletConnectQrCodeUri = null;
      });

      // Generate new QR code
      await startWalletConnection({
        provider: 'WalletConnect',
        walletKey,
        networkType: 'evm',
      });
    } catch (error) {
      // Handle errors normally - "proposal expired" errors are filtered upstream in useIsWalletConnecting
      handleError({error, intl, context: 'wallet_connection'});
    }
  }, [startWalletConnection, walletKey, intl]);

  const countdown = useExpiryCountdown(expiryTimestamp, refreshQrCode);

  useSingleRunEffect(() => {
    // Initial connection setup
    refreshQrCode();
  });

  // Clear QR code URI on unmount to prevent stale QR code on next mount
  useEffect(() => {
    return () => {
      setValue((state) => {
        state.walletConnection.walletConnectQrCodeUri = null;
      });
    };
  }, []);

  return (
    <view.div
      css={{
        stack: 'y',
        alignX: 'center',
        alignY: 'center',
        borderRadius: 'small',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        paddingY: 'large',
        paddingX: 'space.150',
        gap: 'medium',
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'xsmall',
          width: QR_CODE_SIZE,
          alignX: 'center',
        }}
      >
        <Text type="heading">
          {intl.formatMessage(messages.title, {walletProvider: name})}
        </Text>
        <Text type="body" css={{textAlign: 'center'}}>
          {intl.formatMessage(messages.description)}
        </Text>
      </view.div>
      {qrCodeUri ? (
        <QrCode qrCodeUri={qrCodeUri} iconUrl={iconUrl} name={name} />
      ) : (
        <Skeleton css={{width: '266px', height: '266px'}} />
      )}
      {countdown.timeLeft > 0 ? (
        <Text type="body" css={{textAlign: 'center'}}>
          {intl.formatMessage(messages.countdown, {
            timeRemaining: countdown.formatTime(),
          })}
        </Text>
      ) : null}
    </view.div>
  );
};
